import { useMutation, UseMutationOptions, useQuery, UseQueryOptions } from '@tanstack/react-query';
import { apiClient } from '../../lib/util';
import {
  PaychexAPICredentials,
  PaychexAPICredentialsUpdate,
  PaychexCompaniesResponse,
  PaychexPayrollSync,
  PaychexPayrollSyncDetail,
  PayrollSyncStatus
} from '@pxtra-client-lib-legacy/gen';

const PAYCHEX_SYNC_POLL_INTERVAL = 4000;

export function listPaychexPayrollSyncsQuery(companyId: string) {
  return useQuery<PaychexPayrollSync[]>({
    queryKey: ['company', companyId, 'paychex', 'sync'],
    queryFn: async () => {
      const logs = await apiClient.integrations.companiesPaychexSyncList({
        parentCompany: companyId ?? 'MISSING_COMP_ID'
      });

      return logs.results;
    },
    refetchInterval: (query) =>
      query.state.data?.some((sync) => sync.processing_state === 'PROCESSING')
        ? PAYCHEX_SYNC_POLL_INTERVAL
        : false
  });
}

export function retrievePaychexPayrollSyncQuery(companyId: string, syncId: string) {
  return useQuery<PaychexPayrollSyncDetail>({
    queryKey: ['company', companyId, 'paychex', 'sync', syncId],
    queryFn: async () => {
      const log = await apiClient.integrations.companiesPaychexSyncRetrieve({
        parentCompany: companyId ?? 'MISSING_COMP_ID',
        id: syncId
      });

      return log;
    },
    refetchInterval: (query) =>
      query.state.data?.processing_state === 'PROCESSING' ? PAYCHEX_SYNC_POLL_INTERVAL : false
  });
}

export function triggerPaychexPayrollSyncMutation(
  options?: Omit<
    UseMutationOptions<PayrollSyncStatus, Error, { companyId: string; accountingPeriod: string }>,
    'mutationFn'
  >
) {
  return useMutation<PayrollSyncStatus, Error, { companyId: string; accountingPeriod: string }>({
    mutationFn: async ({ companyId, accountingPeriod }) =>
      apiClient.integrations.companiesPaychexSyncTriggerCreate({
        parentCompany: companyId ?? 'MISSING_COMP_ID',
        requestBody: {
          accounting_period: accountingPeriod
        }
      }),
    ...options
  });
}

export function useCreatePaychexApiCredentials(
  companyId: string,
  options?: Omit<
    UseMutationOptions<PaychexAPICredentials, Error, { api_url: string; api_key: string }>,
    'mutationFn'
  >
) {
  return useMutation<PaychexAPICredentials, Error, { api_url: string; api_key: string }>({
    mutationFn: async ({ api_url, api_key }: { api_url: string; api_key: string }) =>
      apiClient.integrations.companiesPaychexCreate({
        parentCompany: companyId ?? 'MISSING_COMP_ID',
        requestBody: { api_url, api_key }
      }),
    ...options
  });
}

export function useFetchPaychexCompanies(
  companyId: string,
  options?: Omit<UseQueryOptions<PaychexCompaniesResponse>, 'queryKey' | 'queryFn'>
) {
  return useQuery<PaychexCompaniesResponse>({
    queryKey: ['company', companyId, 'paychex', 'companies'],
    queryFn: () =>
      apiClient.integrations.companiesPaychexCompaniesRetrieve({
        parentCompany: companyId
      }),
    ...options
  });
}

export function useFetchPaychexApiCredentials(
  companyId: string,
  options?: Omit<UseQueryOptions<PaychexAPICredentials>, 'queryKey' | 'queryFn'>
) {
  return useQuery<PaychexAPICredentials, Error>({
    queryKey: ['company', companyId, 'paychex', 'credentials'],
    queryFn: () =>
      apiClient.integrations.companiesPaychexCredentialsRetrieve({
        parentCompany: companyId
      }),
    ...options
  });
}

export interface PaychexApiCredentialsUpdateError extends Error {
  body: { errors: string[] };
}

export function useUpdatePaychexApiCredentials(
  companyId: string,
  options?: Omit<
    UseMutationOptions<
      PaychexAPICredentialsUpdate,
      PaychexApiCredentialsUpdateError,
      {
        api_url: string;
        api_key: string;
        paychex_company_id: string;
        id: PaychexAPICredentials['id'];
      }
    >,
    'mutationFn'
  >
) {
  return useMutation<
    PaychexAPICredentialsUpdate,
    PaychexApiCredentialsUpdateError,
    {
      api_url: string;
      api_key: string;
      paychex_company_id: string;
      id: PaychexAPICredentials['id'];
    }
  >({
    mutationFn: async ({ api_url, api_key, paychex_company_id, id }) =>
      apiClient.integrations.companiesPaychexPartialUpdate({
        id: id,
        parentCompany: companyId,
        requestBody: { api_url, api_key, paychex_company_id }
      }),
    ...options
  });
}
